import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LangConstant, AppConstant, SystemConstant } from "const";
import { ConfirmDialog, PageTitle } from "components";
import {
  useDeleteDepartment,
  useDeleteUnit,
  useDepartmentList,
  useDepartmentUserList,
  useUnitList,
  useUnitUserList,
} from "hooks";
import {
  ActionPopover,
  CreateChannelDialog,
  DepartmentDialog,
  UnitDialog,
  UnitListHeader,
  UnitListTable,
  MemberDialog,
  AddMemberDialog,
  selectAllUnitValue,
} from "pages/CompanyManagement/components";
import { SystemUtils } from "utils";

const DepartmentManagement = () => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);
  const sidebarTitles = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });

  const {
    unitList,
    totalUnitList,
    totalPage,
    page,
    searchValue,
    isLoading: isLoadingUnitList,
    handleSearchUnit,
    handleChangePage,
  } = useUnitList();

  const { departmentList, handleToggleShowDepartment } = useDepartmentList();
  const { deleteUnit } = useDeleteUnit();
  const { deleteDepartment } = useDeleteDepartment();

  const [actionPopoverConfig, setActionPopoverConfig] = useState({
    anchorEl: null,
    selectedRow: null,
    type: AppConstant.COMPANY_MANAGEMENT_TYPE.unit,
  });

  const [unitDialogState, setUnitDialogState] = useState({ open: false, selectedUnit: null });
  const [deleteDialogState, setDeleteDialogState] = useState({ open: false, selectedRow: null, title: "" });
  const [departmentDialogState, setDepartmentDialogState] = useState({ open: false, selectedUnit: null, selectedDepartment: null });
  const [channelDialogState, setChannelDialogState] = useState({ open: false, selectedUnit: null, selectedDepartment: null });
  const [addMemberDialogState, setAddMemberDialogState] = useState({ open: false, searchValue: "", selectedUnitId: selectAllUnitValue, page: SystemConstant.ACTIVE_PAGINATION });
  const [memberDialogState, setMemberDialogState] = useState({ open: false, selectedDepartment: null, searchValue: "", page: SystemConstant.ACTIVE_PAGINATION });

  const activeDepartment = memberDialogState.open
    ? memberDialogState.selectedDepartment
    : channelDialogState.selectedDepartment || actionPopoverConfig.selectedRow;

  const { departmentUserList, totalPages, currentPageUsers } = useDepartmentUserList(
    activeDepartment?.departmentId,
    activeDepartment?.unitId,
    memberDialogState.searchValue,
    memberDialogState.page,
  );

  const activeUnitIdForUsers = addMemberDialogState.open
    ? addMemberDialogState.selectedUnitId
    : (channelDialogState.selectedUnit?.unitId || channelDialogState.selectedDepartment?.unitId || "");

  const {
    unitUserList,
    totalPages: totalPagesUnit,
    currentPageUsers: currentPageUsersUnit,
  } = useUnitUserList(
    activeUnitIdForUsers,
    addMemberDialogState.open ? addMemberDialogState.searchValue : memberDialogState.searchValue,
    addMemberDialogState.open ? addMemberDialogState.page : memberDialogState.page,
  );

  const handleOpenAction = (event, selectedRow, type) => {
    setActionPopoverConfig(s => ({ ...s, anchorEl: event.target, selectedRow, type }));
  };
  const handleCloseAction = () => {
    setActionPopoverConfig(s => ({ ...s, anchorEl: null }));
  };
  const handleOpenUnitDialog = data => {
    setUnitDialogState({ open: true, selectedUnit: data || null });
  };
  const handleCloseUnitDialog = () => {
    setUnitDialogState({ open: false, selectedUnit: null });
  };
  const handleOpenDepartmentDialog = (department, unit) => {
    setDepartmentDialogState({ open: true, selectedUnit: unit, selectedDepartment: department });
    handleCloseAction();
  };
  const handleCloseDepartmentDialog = () => {
    setDepartmentDialogState(s => ({ ...s, open: false, selectedDepartment: null }));
  };
  const handleOpenMemberManagement = () => {
    setMemberDialogState(s => ({
      ...s,
      open: true,
      selectedDepartment: actionPopoverConfig.selectedRow,
      searchValue: "",
      page: SystemConstant.ACTIVE_PAGINATION,
    }));
    handleCloseAction();
  };
  const handleCloseMemberManagement = () => {
    setMemberDialogState(s => ({ ...s, open: false, searchValue: "", page: SystemConstant.ACTIVE_PAGINATION }));
  };
  const handleOpenCreateChannel = () => {
    setChannelDialogState({
      open: true,
      selectedUnit: actionPopoverConfig.type === AppConstant.COMPANY_MANAGEMENT_TYPE.unit ? actionPopoverConfig.selectedRow : null,
      selectedDepartment: actionPopoverConfig.type === AppConstant.COMPANY_MANAGEMENT_TYPE.unit ? null : actionPopoverConfig.selectedRow,
    });
    handleCloseAction();
  };
  const handleCloseCreateChannel = () => {
    setChannelDialogState(s => ({ ...s, open: false }));
  };
  const handleEdit = () => {
    if (actionPopoverConfig.type === AppConstant.COMPANY_MANAGEMENT_TYPE.unit) {
      handleOpenUnitDialog(actionPopoverConfig.selectedRow);
    } else {
      handleOpenDepartmentDialog(actionPopoverConfig.selectedRow, null);
    }
    handleCloseAction();
  };
  const handleOpenDeleteDialog = () => {
    setDeleteDialogState({
      open: true,
      selectedRow: actionPopoverConfig.selectedRow,
      title: getLabel(
        actionPopoverConfig.type === AppConstant.COMPANY_MANAGEMENT_TYPE.unit
          ? LangConstant.TXT_CONFIRM_DELETE_UNIT
          : LangConstant.TXT_CONFIRM_DELETE_DEPARTMENT,
      ),
    });
    handleCloseAction();
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogState(s => ({ ...s, open: false, selectedRow: null }));
  };
  const handleDelete = () => {
    if (actionPopoverConfig.type === AppConstant.COMPANY_MANAGEMENT_TYPE.unit) {
      deleteUnit({ unitId: deleteDialogState.selectedRow.unitId, onClose: handleCloseDeleteDialog });
    } else {
      deleteDepartment({ unitId: deleteDialogState.selectedRow.unitId, departmentId: deleteDialogState.selectedRow.departmentId, onClose: handleCloseDeleteDialog });
    }
  };
  const handleFilterMember = payload => {
    setMemberDialogState(s => ({ ...s, ...payload }));
  };
  const handleFilterAddMember = payload => {
    setAddMemberDialogState(s => ({ ...s, ...payload }));
  };
  const handleOpenAddMemberDialog = () => {
    setAddMemberDialogState(s => ({
      ...s,
      open: true,
      selectedDepartment: memberDialogState.selectedDepartment,
      selectedUnitId: selectAllUnitValue,
      searchValue: "",
      page: SystemConstant.ACTIVE_PAGINATION,
    }));
  };
  const handleCloseAddMemberDialog = () => {
    setAddMemberDialogState(s => ({
      ...s,
      open: false,
      searchValue: "",
      selectedUnitId: selectAllUnitValue,
      page: SystemConstant.ACTIVE_PAGINATION,
    }));
  };

  useEffect(() => {
    SystemUtils.openCircleLoading(Boolean(isLoadingUnitList));
  }, [isLoadingUnitList]);

  return (
    <Fragment>
      <PageTitle>{sidebarTitles.departmentManagement}</PageTitle>
      <div className="card" style={{ padding: 30, borderRadius: 4 }}>
        <div className="card-body" style={{ padding: 0 }}>
          <UnitListHeader
            searchValue={searchValue}
            handleSearchUnit={handleSearchUnit}
            onOpenUnitDialog={handleOpenUnitDialog}
          />
          <UnitListTable
            unitList={unitList}
            totalPage={totalPage}
            page={page}
            departmentList={departmentList}
            onToggleShowDepartment={handleToggleShowDepartment}
            onChangePage={handleChangePage}
            onOpenAction={handleOpenAction}
          />
          <ActionPopover
            anchorEl={actionPopoverConfig.anchorEl}
            open={Boolean(actionPopoverConfig.anchorEl)}
            selectedRow={actionPopoverConfig.selectedRow}
            type={actionPopoverConfig.type}
            onCloseAction={handleCloseAction}
            onOpenDepartmentDialog={handleOpenDepartmentDialog}
            onOpenMemberManagement={handleOpenMemberManagement}
            onCreateChannel={handleOpenCreateChannel}
            onEdit={handleEdit}
            onDelete={handleOpenDeleteDialog}
          />
          <UnitDialog open={unitDialogState.open} selectedUnit={unitDialogState.selectedUnit} onClose={handleCloseUnitDialog} />
          <ConfirmDialog open={deleteDialogState.open} title={deleteDialogState.title} onDeny={handleCloseDeleteDialog} onConfirm={handleDelete} />
          <DepartmentDialog open={departmentDialogState.open} unitList={totalUnitList} selectedUnit={departmentDialogState.selectedUnit} selectedDepartment={departmentDialogState.selectedDepartment} onClose={handleCloseDepartmentDialog} />
          <CreateChannelDialog open={channelDialogState.open} selectedUnit={channelDialogState.selectedUnit} selectedDepartment={channelDialogState.selectedDepartment} departmentUserList={departmentUserList} unitUserList={unitUserList} onClose={handleCloseCreateChannel} />
          <MemberDialog open={memberDialogState.open} selectedDepartment={memberDialogState.selectedDepartment} totalPages={totalPages} currentPageUsers={currentPageUsers} page={memberDialogState.page} onFilter={handleFilterMember} onOpenAddMemberDialog={handleOpenAddMemberDialog} onClose={handleCloseMemberManagement} />
          <AddMemberDialog open={addMemberDialogState.open} selectedDepartment={memberDialogState.selectedDepartment} unitList={totalUnitList} totalPages={totalPagesUnit} page={addMemberDialogState.page} currentPageUsers={currentPageUsersUnit} departmentUserList={departmentUserList} onFilter={handleFilterAddMember} onClose={handleCloseAddMemberDialog} />
        </div>
      </div>
    </Fragment>
  );
};

export default DepartmentManagement;
